import { FC, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import AnalyticsReportsApi from 'api/analytics-reports';
import type { ReportRun } from 'api/analytics-reports/types';
import { ApiClientError } from 'api/client';
import { isUnauthorizedApiClientError } from 'api/client/isUnauthorizedApiClientError';
import { useToast } from 'app/hooks/useToast';
import { queryKey } from 'constants/queryKey';
import { useTranslation } from 'react-i18next';
import {
  ActiveReportRun,
  useActiveReportRuns,
} from 'app/modules/analytics/modules/reports/context/ActiveReportRunsContext';
import { dismissAnalyticsReportGeneratingToast } from 'app/modules/analytics/modules/reports/utils/analyticsReportGeneratingToast';
import {
  buildReportExportFileName,
  downloadReportExport,
} from 'app/utils/reportPdfExport';

const FAST_POLL_MS = 3000;
const SLOW_POLL_MS = 10000;
const FAST_POLL_WINDOW_MS = 60_000;

interface ActiveReportRunPollerProps {
  run: ActiveReportRun;
}

const ActiveReportRunPoller: FC<ActiveReportRunPollerProps> = ({ run }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { removeActiveRun, updateActiveRunStatus } = useActiveReportRuns();
  const pollingStartedAtRef = useRef(Date.now());
  const handledStatusRef = useRef<ReportRun['status'] | null>(null);

  const runQuery = useQuery<ReportRun, ApiClientError>(
    queryKey.analyticsReportRun(run.reportId, run.runId),
    () => AnalyticsReportsApi.getReportRun(run.reportId, run.runId),
    {
      refetchInterval: (data) => {
        if (!data || data.status === 'completed' || data.status === 'failed') {
          return false;
        }

        const elapsed = Date.now() - pollingStartedAtRef.current;
        return elapsed < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;
      },
      retry: 0,
      onError: (error) => {
        if (isUnauthorizedApiClientError(error)) {
          return;
        }

        toast(
          error?.userMessage ?? t('analyticsReports.generateError'),
          'error',
        );
        dismissAnalyticsReportGeneratingToast(run.runId);
        removeActiveRun(run.runId);
      },
    },
  );

  useEffect(() => {
    const status = runQuery.data?.status;

    if (!status) {
      return;
    }

    if (status === 'pending' || status === 'running') {
      updateActiveRunStatus(run.runId, status);
      return;
    }

    if (handledStatusRef.current === status) {
      return;
    }

    handledStatusRef.current = status;

    dismissAnalyticsReportGeneratingToast(run.runId);

    if (status === 'completed') {
      if (run.autoDownloadOnComplete) {
        void AnalyticsReportsApi.resolveReportDownloadUrl(
          run.reportId,
          run.format,
        )
          .then((url) => {
            if (!url) {
              toast(t('analyticsReports.viewPdfNotReady'), 'error');
              return;
            }

            void downloadReportExport(
              url,
              buildReportExportFileName(run.name, run.reportId, run.format),
            );
          })
          .catch((error: unknown) => {
            if (isUnauthorizedApiClientError(error as ApiClientError)) {
              return;
            }

            const apiError = error as ApiClientError;
            toast(
              apiError.userMessage ?? t('analyticsReports.viewPdfError'),
              'error',
            );
          });
      } else {
        toast(t('analyticsReports.generateSuccess'), 'success');
      }

      queryClient.invalidateQueries(queryKey.analyticsReports());
      removeActiveRun(run.runId);
      return;
    }

    toast(t('analyticsReports.generateError'), 'error');
    removeActiveRun(run.runId);
  }, [
    queryClient,
    removeActiveRun,
    run.autoDownloadOnComplete,
    run.format,
    run.name,
    run.reportId,
    run.runId,
    runQuery.data?.status,
    t,
    toast,
    updateActiveRunStatus,
  ]);

  return null;
};

const ReportRunPollingDriver: FC = () => {
  const { activeRuns } = useActiveReportRuns();

  return (
    <>
      {activeRuns.map((run) => (
        <ActiveReportRunPoller key={run.runId} run={run} />
      ))}
    </>
  );
};

export default ReportRunPollingDriver;
